import { isDesktopShell } from "../../../common/hooks/useIsDesktopView";
import { articleDetailQuery } from "../../../features/article/hooks/useArticleData";
import { useTranslation } from "react-i18next";
import { useApiQuery } from "../../../services/api";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { createMemoryStore } from "../../../lib/store";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import { fieldBand, indexedIn } from "../../../domains/journal/journalRank";
import {
  assistantChatSource,
  assistantSourcesRefetchInterval,
  toChatSource,
  unlistedChatSource,
} from "../../../domains/gpt/chatSource";
import type { GptSourceRef } from "../../../domains/gpt/transcript";
import {
  ChatSourceSheet,
  type ChatSourceSheetContent,
} from "../../ui/ChatSourceSheet";
import { journalStatesAnything } from "../../works-score/WorksScoreScreen";

/**
 * What the open sheet is showing.
 *
 * `listed` — one of the turn's own sources: the row in the Sources tab and the
 * numbered chip in the answer both open this one.
 * `assistant` — a source of an assistant answer, opened from its row or its chip.
 * The sheet reads it from the same query as the Sources tab, so both state what
 * the api resolved about it.
 * `unlisted` — a citation the turn's list does not hold, opened from its chip in
 * the answer. It carries nothing beyond the link the answer wrote, and the sheet
 * says exactly that much. Every citation opens a sheet, resolved or not: a
 * citation that instead navigated away could not explain itself, and one that
 * did nothing at all would read as broken.
 */
export type OpenedGptSource =
  | {
      kind: "listed";
      source: GptSourceRef;
      /** The number the answer's inline citation chip points at, 1-based. */
      citationNumber: number;
    }
  | {
      kind: "assistant";
      conversationId: string;
      runId: string;
      // A cited source is found by the number its chips show, another one by its evidence row.
      target:
        | { kind: "cited"; number: number }
        | { kind: "consulted"; evidenceId: string };
    }
  | { kind: "unlisted"; href: string; label: string };

/**
 * Which source's sheet is open, for the whole conversation.
 *
 * A module slot rather than component state, and shared rather than owned by
 * one component, because the sheet opens from two places in two different
 * subtrees: a row of the Sources tab, and an inline citation chip in the
 * answer. The sheet itself is modal, so exactly one can be open in the app.
 */
export const useSelectedGptSource = createMemoryStore<OpenedGptSource | null>(
  null
);

/**
 * One cited source, opened from its row in the Sources tab or from its numbered
 * chip in the answer. Both open the same reference, or the same assistant row,
 * so a source reads the same whichever one the reader tapped.
 *
 * Everything is read on open, nothing on the turn: a Juisci source resolves its
 * identity and its journal rank in one article read, and a snapshot has the
 * journal it names looked up. Ten cited sources therefore cost nothing until
 * one of them is actually opened.
 */
export default function GPTSourceSheet({
  opened,
  roomId,
  onClose,
}: {
  // Null closes the sheet.
  opened: OpenedGptSource | null;
  roomId: string | null;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { articleLink } = usePageLinks(roomId);
  // Gated on the SAME flag as the article page's works score, by decision
  // (Menie): one switch for both, because both state the same fact in the same
  // words and must never disagree about one paper.
  //
  // A cited source in the general chat MAY state the verdict, and a cited
  // source under an Evidence answer may not. The line is not who chose the
  // paper, it is whether the screen already grades it: an Evidence answer
  // states its own JuisciGrade letter over the whole body of sources, so a
  // second verdict per source contradicts the letter above it. Nothing grades
  // the paper on this sheet, so the verdict stands alone here. See
  // docs/data-ingestion/risk-of-bias.md, the exposure rule.
  //
  // The JOURNAL half deliberately stays OUTSIDE this gate: an impact factor,
  // a SIGAPS band and an indexing list are public facts about the venue, not
  // a judgement Juisci generated, so the exposure rule does not reach them.
  const showLevel = useFeatureIsOn("WEBAPP_RISK_OF_BIAS");

  // An unlisted citation is a link and a label, nothing the app can read more
  // about: the article and journal reads below are for a listed or an assistant source.
  const source =
    opened === null || opened.kind !== "listed" ? null : opened.source;

  // Same query and key as the Sources tab, so a row opens on what the list already holds.
  const assistantSources = useApiQuery(
    "/assistant/conversations/:conversationId/runs/:runId/sources",
    opened === null || opened.kind !== "assistant"
      ? false
      : {
          param: {
            conversationId: opened.conversationId,
            runId: opened.runId,
          },
          // A citation in the answer opens the sheet without the list mounted.
          refetchInterval: assistantSourcesRefetchInterval,
        }
  );
  // Null while the query loads or failed, and when the run no longer lists the source.
  const assistantSource = (() => {
    if (opened === null || opened.kind !== "assistant") return null;
    if (assistantSources.state !== "success") return null;
    const { target } = opened;
    switch (target.kind) {
      case "cited": {
        const row = assistantSources.body.cited.find(
          (cited) => cited.number === target.number
        );
        return row === undefined ? null : row.source;
      }
      case "consulted": {
        const row = assistantSources.body.consulted.find(
          (consulted) => consulted.source.evidenceId === target.evidenceId
        );
        return row === undefined ? null : row.source;
      }
      default:
        throw new Error(target satisfies never);
    }
  })();

  // The article behind a Juisci source, read by SLUG: only the detail endpoint
  // recomputes `journalRank` and `evidenceLevel`. The base serializer leaves
  // both null, so no other read can stand in for it.
  const internalId =
    source === null || source.provenance !== "juisci_internal"
      ? null
      : source.id;
  const refSlug =
    assistantSource !== null && assistantSource.kind === "article"
      ? assistantSource.article.slug
      : source === null || source.provenance !== "juisci_internal"
        ? null
        : source.slug;

  // A streamed reference reaches this sheet before the batch read gave it a
  // slug, and the ref is captured when the reader taps: it never refreshes, so
  // the sheet would state an identity and nothing else for as long as it stays
  // open. The id is always there, and the batch read carries the slug, so one
  // extra hop recovers the works score. Only asked when the ref has no slug.
  const byId = useApiQuery(
    "/webapp/public/articles/batch",
    refSlug === null && internalId !== null
      ? { query: { ids: internalId }, durable: true }
      : false
  );
  const batchedSlug =
    byId.state === "success" ? byId.body.at(0)?.slug : undefined;
  const slug =
    refSlug !== null ? refSlug : batchedSlug === undefined ? null : batchedSlug;
  const detail = useApiQuery(
    "/webapp/public/articles/:slug",
    slug === null
      ? false
      : { param: { slug }, query: articleDetailQuery(roomId), durable: true }
  );
  const article = detail.state === "success" ? detail.body : null;

  // A source Juisci never crawled still names its journal, and that name is
  // enough to find the journal's rank. Only asked when there is no article to
  // read it from, so a Juisci source never issues both.
  const citedJournal =
    assistantSource !== null
      ? assistantSource.kind === "external" &&
        assistantSource.container !== null &&
        assistantSource.container.kind === "journal"
        ? assistantSource.container.name
        : null
      : source === null || source.provenance === "juisci_internal"
        ? null
        : source.provenance === "uploaded_document"
          ? null
          : source.journal;
  const citedRank = useApiQuery(
    "/webapp/public/journals/rank",
    citedJournal === null
      ? false
      : { query: { name: citedJournal }, durable: true }
  );

  // The article read is in flight: its journal and its study are not known yet.
  const articleLoading =
    slug !== null
      ? detail.state === "pending"
      : refSlug === null && internalId !== null && byId.state === "pending";
  // A placeholder holds the journal block while a read can still state it: the
  // article, the rank of the journal the source names, or the source itself
  // while the api resolves its journal. The sheet opens on the identity it
  // already has rather than waiting on the read.
  const journalLoading =
    articleLoading ||
    (citedJournal !== null && citedRank.state === "pending") ||
    (assistantSource !== null &&
      assistantSource.kind === "external" &&
      assistantSource.resolution === "pending");
  // A journal Juisci does not hold means no journal block.
  const rank =
    article !== null
      ? article.journalRank
      : citedRank.state === "success"
        ? citedRank.body
        : null;
  const band = rank === null ? null : fieldBand(t, rank);

  const standing =
    rank === null
      ? null
      : {
          // The identity line above already names the journal.
          journalName: null,
          impactFactor: rank.impactFactor,
          band,
          indexing: indexedIn(rank),
          isCddRecommended: rank.cdd,
        };
  // A journal Juisci holds but has neither ranked nor indexed states nothing:
  // no block, and no divider above it.
  const journal = journalLoading
    ? "loading"
    : standing !== null && journalStatesAnything(standing)
      ? standing
      : null;
  // The article read above already carries the appraisal, so the works score
  // costs this sheet nothing beyond what it was reading anyway. Flag-gated: see
  // the note on `showLevel`.
  //
  // ROLLOUT: no room gate here either. The B2B rule belongs to the ARTICLE and
  // the api applies it, so this sheet reads the null it already handles (see
  // `ArticleSectionWorksScore`).
  //
  // Stated in the language of the article that was read, not of the reader's
  // app: the appraisal follows the work (docs/data-ingestion/risk-of-bias.md).
  // That is also the language the api answered its free-text values in, so both
  // halves of the card agree by construction.
  const level = !showLevel
    ? null
    : articleLoading
      ? "loading"
      : article === null || article.appraisal === null
        ? null
        : { appraisal: article.appraisal, language: article.language };
  const articleById = new Map(article === null ? [] : [[article.id, article]]);

  const content: ChatSourceSheetContent | "loading" | "failed" | null = (():
    ChatSourceSheetContent | "loading" | "failed" | null => {
    if (opened === null) return null;
    switch (opened.kind) {
      // Nothing to look up and no journal to name: the sheet states the link
      // the answer wrote and hands the reader the way out.
      case "unlisted":
        return {
          source: unlistedChatSource(opened),
          journal: null,
          level: null,
        };
      case "listed":
        return {
          // Index 0: the sheet draws one source, so the key it builds is never
          // compared against another.
          source: toChatSource(opened.source, 0, articleById, articleLink),
          journal,
          level,
        };
      case "assistant":
        // A chip can open the sheet before the Sources tab ever read the list.
        if (assistantSources.state === "pending") return "loading";
        if (assistantSources.state === "error") return "failed";
        return assistantSource === null
          ? null
          : {
              source: assistantChatSource(
                assistantSource,
                articleById,
                articleLink
              ),
              journal,
              level,
            };
      default:
        throw new Error(opened satisfies never);
    }
  })();

  return (
    <ChatSourceSheet
      content={content}
      citationNumber={
        opened === null || opened.kind === "unlisted"
          ? null
          : opened.kind === "listed"
            ? opened.citationNumber
            : opened.target.kind === "cited"
              ? opened.target.number
              : null
      }
      presentation={isDesktopShell() ? "dialog" : "sheet"}
      isOpen={opened !== null}
      onClose={onClose}
    />
  );
}
